import React, { useState } from 'react';
import { Button } from './ui/button';
import { Cloud, AlertTriangle } from 'lucide-react';
import { CoatingActualsDataModal } from './CoatingActualsDataModal';

interface PressureSystem {
    id: string;
    name: string;
    actual: number;
    setpoint: number;
    unit: string;
    status: 'normal' | 'warning' | 'danger';
    lastChanged?: boolean;
}

interface PressureControlDashboardProps {
    pressureView: 'dashboard' | 'grid' | 'table' | 'warnings' | 'danger';
    onViewChange: (view: 'dashboard' | 'grid' | 'table' | 'warnings' | 'danger') => void;
    apiResponse?: any;
    edgeUid?: string;
    device?: import('../features/device/types/device').Device;
}

const formatPressureValue = (value: number): string => value.toFixed(2);

export const PressureControlDashboard: React.FC<PressureControlDashboardProps> = ({
    pressureView,
    onViewChange,
    apiResponse,
    edgeUid,
    device,
}) => {
    const [isViewAllOpen, setIsViewAllOpen] = useState(false);
    // Helper function to calculate pressure system status based on deviation and pressure thresholds
    const calculatePressureStatus = (
        deviation: number,
        minPressure: number | null | undefined,
        maxPressure: number | null | undefined,
        underMinPressure: number,
        overMaxPressure: number
    ): 'normal' | 'warning' | 'danger' => {
        if (minPressure !== undefined && minPressure !== null && 
            maxPressure !== undefined && maxPressure !== null) {
            // Danger zone: deviation < under_minimum_pressure OR deviation > over_maximum_pressure
            const isDanger = deviation < underMinPressure || deviation > overMaxPressure;
            // Normal zone: deviation >= minimum_pressure AND deviation <= maximum_pressure
            const isInNormalRange = deviation >= minPressure && deviation <= maxPressure;
            // Warning zone: (deviation >= under_minimum_pressure AND deviation < minimum_pressure) 
            //               OR (deviation > maximum_pressure AND deviation <= over_maximum_pressure)
            const isWarning = !isDanger && !isInNormalRange && 
                ((deviation >= underMinPressure && deviation < minPressure) || 
                 (deviation > maxPressure && deviation <= overMaxPressure));

            if (isDanger) {
                return 'danger';
            } else if (isInNormalRange) {
                return 'normal';
            } else if (isWarning) {
                return 'warning';
            }
        }
        
        // Fallback: if no pressure range configured, use default thresholds
        const isDangerFallback = Math.abs(deviation) > 10; // 2x default warning threshold
        const isWarningFallback = Math.abs(deviation) > 5;
        
        if (isDangerFallback) {
            return 'danger';
        } else if (isWarningFallback) {
            return 'warning';
        } else {
            return 'normal';
        }
    };

    // Map API data to pressure systems - only use API data, no fallback
    const getPressureSystemsFromAPI = (): PressureSystem[] => {
        if (!apiResponse?.lastActuals || !apiResponse?.lastSetpoint) {
            return []; // Return empty array if API data is not available
        }

        const actuals = apiResponse.lastActuals;
        const setpoints = apiResponse.lastSetpoint;
        const minimumPressure = apiResponse?.minimum_pressure;
        const maximumPressure = apiResponse?.maximum_pressure;
        const underMinPressure = apiResponse?.under_minimum_pressure ?? 0;
        const overMaxPressure = apiResponse?.over_maximum_pressure ?? 0;

        // Map API field names to display names and units
        const pressureFields = [
            { apiKey: 'mouldPressure', name: 'Mould Pressure Act', id: 'MP', unit: 'Bar' },
            { apiKey: 'lockBarPressure', name: 'Lock Bar Pressure Act', id: 'LBP', unit: 'Bar' },
            { apiKey: 'lockBarDifferentialPressure', name: 'Lock Bar Differential Pressure Act', id: 'LBD', unit: 'Bar' },
            { apiKey: 'backPressure', name: 'Back Pressure Act', id: 'BP', unit: 'Bar' },
            { apiKey: 'accumulatorInfillPressure_1', name: 'ACC1 Infill Pressure Act', id: 'ACC1', unit: 'Bar' },
            { apiKey: 'hydraulicPumpPressure', name: 'Hydraulic Pump Pressure Act', id: 'HP', unit: 'Bar' },
        ];

        return pressureFields
            .filter(field => actuals[field.apiKey] !== undefined && actuals[field.apiKey] !== null)
            .map(field => {
                const actual = actuals[field.apiKey] ?? 0;
                const setpoint = setpoints[field.apiKey] ?? 0;
                // Calculate deviation as setpoint - actual
                const deviation = setpoint - actual;
                
                // Determine status using helper function
                const status = calculatePressureStatus(
                    deviation,
                    minimumPressure,
                    maximumPressure,
                    underMinPressure,
                    overMaxPressure
                );

                return {
                    id: field.id,
                    name: field.name,
                    actual,
                    setpoint,
                    unit: field.unit,
                    status,
                    lastChanged: false,
                };
            });
    };

    const displayPressureData = getPressureSystemsFromAPI();
    return (
        <>
        <div className="mt-6 bg-white rounded-lg border border-gray-200">
            <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
                <div className="flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-3">
                        <h3 className="flex min-w-0 flex-wrap items-center gap-2 text-base font-medium text-gray-900 sm:text-lg">
                            <Cloud className="h-5 w-5 shrink-0 text-purple-400" />
                            Pressure Control Dashboard
                            <span className="text-sm font-normal text-gray-500">({displayPressureData.length} systems)</span>
                        </h3>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={!edgeUid}
                            onClick={() => setIsViewAllOpen(true)}
                            className="shrink-0"
                        >
                            View All
                        </Button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant={pressureView === 'dashboard' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('dashboard')}
                            className="text-xs"
                        >
                            Dashboard
                        </Button>
                        <Button
                            variant={pressureView === 'grid' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('grid')}
                            className="text-xs"
                        >
                            Grid
                        </Button>
                        <Button
                            variant={pressureView === 'table' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('table')}
                            className="text-xs"
                        >
                            Table
                        </Button>
                        <Button
                            variant={pressureView === 'warnings' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('warnings')}
                            className="text-xs"
                        >
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            Warnings
                        </Button>
                        <Button
                            variant={pressureView === 'danger' ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => onViewChange('danger')}
                            className="text-xs bg-red-50 hover:bg-red-100"
                        >
                            <AlertTriangle className="h-3 w-3 mr-1 text-red-600" />
                            Danger
                        </Button>
                    </div>
                </div>
            </div>

            <div className="p-6">
                {/* Dashboard View */}
                {pressureView === 'dashboard' && (
                    <div className="space-y-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                            <div className="text-center p-4 bg-green-50 rounded-lg border">
                                <div className="text-2xl font-bold text-green-700">
                                    {displayPressureData.filter(p => p.status === 'normal').length}
                                </div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <div className="h-2 w-2 bg-green-500 rounded-full"></div>
                                    <div className="text-xs text-green-600 font-medium">Normal Systems</div>
                                </div>
                            </div>
                            <div className="text-center p-4 bg-yellow-50 rounded-lg border">
                                <div className="text-2xl font-bold text-yellow-700">
                                    {displayPressureData.filter(p => p.status === 'warning').length}
                                </div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <AlertTriangle className="h-3 w-3 text-yellow-600" />
                                    <div className="text-xs text-yellow-600 font-medium">Warning Systems</div>
                                </div>
                            </div>
                            <div className="text-center p-4 bg-red-50 rounded-lg border">
                                <div className="text-2xl font-bold text-red-700">
                                    {displayPressureData.filter(p => p.status === 'danger').length}
                                </div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <AlertTriangle className="h-3 w-3 text-red-600" />
                                    <div className="text-xs text-red-600 font-medium">Danger Systems</div>
                                </div>
                            </div>
                            <div className="text-center p-4 bg-blue-50 rounded-lg border">
                                <div className="text-lg font-bold text-blue-700">
                                    {displayPressureData.length > 0 ? formatPressureValue(Math.max(...displayPressureData.map(p => p.actual))) : '0.00'} Bar
                                </div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <span className="text-xs">📈</span>
                                    <div className="text-xs text-blue-600 font-medium">Highest Pressure</div>
                                </div>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg border">
                                <div className="text-lg font-bold text-purple-700">
                                    {displayPressureData.length > 0 ? formatPressureValue(Math.min(...displayPressureData.map(p => p.actual))) : '0.00'} Bar
                                </div>
                                <div className="flex items-center justify-center gap-1 mt-2">
                                    <span className="text-xs">📉</span>
                                    <div className="text-xs text-purple-600 font-medium">Lowest Pressure</div>
                                </div>
                            </div>
                        </div>

                        {/* Systems Requiring Attention */}
                        {displayPressureData.filter(p => p.status === 'warning' || p.status === 'danger').length > 0 && (
                            <div className="bg-yellow-50 border rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                                    <h4 className="text-sm font-medium text-yellow-800">Systems Requiring Attention:</h4>
                                </div>
                                <div className="space-y-1">
                                    {displayPressureData
                                        .filter(p => p.status === 'warning' || p.status === 'danger')
                                        .map(p => {
                                            // Calculate deviation as setpoint - actual
                                            const deviation = p.setpoint - p.actual;
                                            return (
                                                <div key={p.id} className={`text-sm ${p.status === 'danger' ? 'text-red-700 font-semibold' : 'text-gray-700'}`}>
                                                    <span className="font-medium">{p.id}:</span> {formatPressureValue(p.actual)}{p.unit}
                                                    <span className={p.status === 'danger' ? 'text-red-700' : 'text-yellow-700'}>
                                                        {' '}({deviation > 0 ? '+' : ''}{formatPressureValue(deviation)} from SP)
                                                        {p.status === 'danger' && ' ⚠️ DANGER'}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Grid View */}
                {pressureView === 'grid' && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {displayPressureData.length === 0 ? (
                            <div className="col-span-full text-center py-12 text-gray-500">
                                <Cloud className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                                <p className="text-lg font-medium">No pressure data available</p>
                            </div>
                        ) : (
                            displayPressureData.map((pressure) => (
                            <div
                                key={pressure.id}
                                className={`p-3 rounded-lg border-2 transition-all ${
                                    pressure.status === 'normal'
                                        ? ' bg-green-50'
                                        : pressure.status === 'danger'
                                            ? ' bg-red-50'
                                            : ' bg-yellow-50'
                                } ${pressure.lastChanged ? 'animate-pulse ring-2 ring-blue-400' : ''}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-gray-700">{pressure.id}</span>
                                    <span className="text-lg">
                                        {pressure.status === 'normal' ? '🟢' : pressure.status === 'danger' ? '🔴' : '🟡'}
                                    </span>
                                </div>
                                <div className={`text-lg font-bold mb-1 ${pressure.lastChanged ? 'text-blue-600' : 'text-gray-800'}`}>
                                    {formatPressureValue(pressure.actual)} {pressure.unit}
                                </div>
                                <div className="text-xs text-gray-600 truncate">
                                    {pressure.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    SP: {formatPressureValue(pressure.setpoint)} {pressure.unit}
                                </div>
                            </div>
                            ))
                        )}
                    </div>
                )}

                {/* Table View */}
                {pressureView === 'table' && (
                    <div className="overflow-auto max-h-96">
                        {displayPressureData.length === 0 ? (
                            <div className="text-center py-12 text-gray-500">
                                <Cloud className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                                <p className="text-lg font-medium">No pressure data available</p>
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parameter</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actual</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Set Point</th>
                                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Delta</th>
                                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {displayPressureData.map((param) => {
                                    // Calculate deviation as setpoint - actual
                                    const deviation = param.setpoint - param.actual;
                                    return (
                                        <tr key={param.id} className={param.lastChanged ? 'bg-blue-50 animate-pulse' : ''}>
                                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{param.name}</td>
                                            <td className={`px-4 py-3 text-sm text-right font-mono ${
                                                param.lastChanged ? 'font-bold text-blue-600' : 'text-gray-900'
                                            }`}>
                                                {formatPressureValue(param.actual)} {param.unit}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-right font-mono text-gray-600">
                                                {formatPressureValue(param.setpoint)} {param.unit}
                                            </td>
                                            <td className={`px-4 py-3 text-sm text-right font-mono ${
                                                deviation > 0 ? 'text-red-600' : 'text-blue-600'
                                            }`}>
                                                {deviation > 0 ? '+' : ''}{formatPressureValue(deviation)}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                    param.status === 'normal'
                                                        ? 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]'
                                                        : param.status === 'danger'
                                                            ? 'text-readable-red bg-[rgba(196,0,31,0.06)]'
                                                            : 'text-[#B38600] bg-[rgba(230,161,0,0.08)]'
                                                }`}>
                                                    {param.status === 'normal' ? '🟢' : param.status === 'danger' ? '🔴' : '🟡'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}

                {/* Warnings View */}
                {pressureView === 'warnings' && (
                    <div className="space-y-3">
                        <h4 className="text-sm font-medium text-yellow-800 mb-3">
                            ⚠️ Systems Requiring Attention ({displayPressureData.filter(p => p.status === 'warning' || p.status === 'danger').length})
                        </h4>
                        {displayPressureData.filter(p => p.status === 'warning' || p.status === 'danger').length === 0 ? (
                            <div className="text-center py-8 text-green-600">
                                ✅ All pressure systems are operating normally
                            </div>
                        ) : (
                            displayPressureData
                                .filter(p => p.status === 'warning' || p.status === 'danger')
                                .map(pressure => {
                                    // Calculate deviation as setpoint - actual
                                    const deviation = pressure.setpoint - pressure.actual;
                                    return (
                                        <div key={pressure.id} className={`p-3 rounded-lg border-2 ${pressure.status === 'danger' ? ' bg-red-50' : ' bg-yellow-50'}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className={`font-bold ${pressure.status === 'danger' ? 'text-red-600' : 'text-yellow-600'}`}>{pressure.id}</span>
                                                    <span className={`text-sm ${pressure.status === 'danger' ? 'text-red-700' : 'text-yellow-700'}`}>{pressure.name}</span>
                                                    {pressure.status === 'danger' && <span className="text-red-600">⚠️ DANGER</span>}
                                                </div>
                                                <span className="text-xl">{pressure.status === 'danger' ? '🔴' : '🟡'}</span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4 text-sm">
                                                <div>
                                                    <span className="text-gray-600">Current:</span>
                                                    <div className={`font-semibold ${pressure.status === 'danger' ? 'text-red-800' : 'text-yellow-800'}`}>{formatPressureValue(pressure.actual)} {pressure.unit}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Set Point:</span>
                                                    <div className="font-semibold text-gray-700">{formatPressureValue(pressure.setpoint)} {pressure.unit}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Deviation:</span>
                                                    <div className={`font-semibold ${
                                                        pressure.status === 'danger' ? 'text-red-600' : (deviation > 0 ? 'text-red-600' : 'text-blue-600')
                                                    }`}>
                                                        {deviation > 0 ? '+' : ''}{formatPressureValue(deviation)} {pressure.unit}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                        )}
                    </div>
                )}

                {/* Danger View */}
                {pressureView === 'danger' && (
                    <div className="space-y-3">
                        <h4 className="text-sm font-medium text-red-800 mb-3">
                            🔴 Systems in Danger ({displayPressureData.filter(p => p.status === 'danger').length})
                        </h4>
                        {displayPressureData.filter(p => p.status === 'danger').length === 0 ? (
                            <div className="text-center py-8 text-green-600">
                                ✅ No systems in danger
                            </div>
                        ) : (
                            displayPressureData
                                .filter(p => p.status === 'danger')
                                .map(pressure => {
                                    // Calculate deviation as setpoint - actual
                                    const deviation = pressure.setpoint - pressure.actual;
                                    return (
                                        <div key={pressure.id} className="p-3 rounded-lg border-2 bg-red-50">
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-red-600 font-bold">{pressure.id}</span>
                                                    <span className="text-sm text-red-700">{pressure.name}</span>
                                                    <span className="text-red-600">⚠️ DANGER</span>
                                                </div>
                                                <span className="text-xl">🔴</span>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4 text-sm">
                                                <div>
                                                    <span className="text-gray-600">Current:</span>
                                                    <div className="font-semibold text-red-800">{formatPressureValue(pressure.actual)} {pressure.unit}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Set Point:</span>
                                                    <div className="font-semibold text-gray-700">{formatPressureValue(pressure.setpoint)} {pressure.unit}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Deviation:</span>
                                                    <div className={`font-semibold text-red-600`}>
                                                        {deviation > 0 ? '+' : ''}{formatPressureValue(deviation)} {pressure.unit}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                        )}
                    </div>
                )}
            </div>
        </div>

        <CoatingActualsDataModal
            isOpen={isViewAllOpen}
            onClose={() => setIsViewAllOpen(false)}
            edgeUid={edgeUid}
            device={device}
            dataType="pressure"
        />
        </>
    );
};

